import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Maximize2, Minimize2, Download, Printer, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { fetchSignedUrl, useMarkFileReviewed } from "../../../lib/fileQueries";

const PREVIEWABLE_EXTENSIONS = new Set(["pdf", "png", "jpg", "jpeg", "gif", "webp"]);

/**
 * Master v5 Prompt 11 — file preview viewer/drawer.
 *
 * A fresh, re-authorized signed URL is fetched EVERY time this opens
 * (never cached/reused from a prior open — § "reauthorize on every
 * open"). PDF/image get focus/fullscreen/download/print; video gets the
 * Bunny Stream signed embed player; DOCX/PPTX get an HONEST error
 * (no fake preview) with a download-only fallback.
 */
export default function FilePreviewDrawer({ open, onOpenChange, workspaceId, fasId, actorRole }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const frameRef = useRef(null);
  const containerRef = useRef(null);
  const markReviewed = useMarkFileReviewed(workspaceId);

  useEffect(() => {
    if (!open || !fasId) { setData(null); setError(null); setFocusMode(false); markReviewed.reset(); return; }
    fetchSignedUrl(workspaceId, fasId).then(setData).catch((e) => setError(e.body?.detail || e.message));
  }, [open, fasId, workspaceId]);

  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setFocusMode((v) => !v);
  }

  function handlePrint() {
    frameRef.current?.contentWindow?.print();
  }

  const isDocxPptx = data && ["docx", "pptx"].includes(data.extension);
  const isVideo = data && data.provider === "bunny_stream";
  const isPreviewable = data && (PREVIEWABLE_EXTENSIONS.has(data.extension) || isVideo);

  return (
    <Dialog open={open} onOpenChange={(o) => { setFocusMode(false); onOpenChange(o); }}>
      <DialogContent
        data-testid="file-preview-drawer"
        className={focusMode ? "max-w-none w-[95vw] h-[92vh]" : "max-w-3xl h-[75vh]"}
      >
        <DialogHeader className="flex-row items-center justify-between">
          <DialogTitle className="font-serif text-ink truncate">{data?.original_filename || "File preview"}</DialogTitle>
          <DialogDescription className="sr-only">Preview of the selected file, with fullscreen, print and download controls.</DialogDescription>
          <div className="flex items-center gap-2">
            {actorRole === "coach" && (
              <Button
                size="sm" variant={markReviewed.isSuccess ? "secondary" : "outline"}
                disabled={markReviewed.isPending || markReviewed.isSuccess}
                onClick={() => markReviewed.mutate(fasId)}
                data-testid="file-preview-mark-reviewed"
              >
                {markReviewed.isSuccess ? <><CheckCircle2 size={14} className="mr-1.5" /> Reviewed</> : "Mark reviewed"}
              </Button>
            )}
            {data?.can_download && (
              <a href={data.signed_url} download={data.original_filename} data-testid="file-preview-download">
                <Button size="sm" variant="outline"><Download size={14} className="mr-1.5" /> Download</Button>
              </a>
            )}
            {!isVideo && isPreviewable && (
              <Button size="sm" variant="outline" onClick={handlePrint} data-testid="file-preview-print">
                <Printer size={14} className="mr-1.5" /> Print
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={toggleFullscreen} data-testid="file-preview-fullscreen-toggle">
              {focusMode ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </Button>
          </div>
        </DialogHeader>

        <div ref={containerRef} className="flex-1 min-h-0 bg-ink/5 rounded-lg overflow-hidden" data-testid="file-preview-body">
          {error ? (
            <div className="flex items-center justify-center h-full text-red-600 text-sm gap-2" data-testid="file-preview-error">
              <AlertTriangle size={16} /> {error}
            </div>
          ) : !data ? (
            <div className="flex items-center justify-center h-full text-ink-soft" data-testid="file-preview-loading">
              <Loader2 size={18} className="animate-spin mr-2" /> Loading…
            </div>
          ) : isDocxPptx ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6" data-testid="file-preview-docx-honest-error">
              <AlertTriangle size={22} className="text-ink-soft" />
              <p className="text-ink-soft text-sm">
                A preview isn't available for Word/PowerPoint files yet — download the original to view it.
              </p>
            </div>
          ) : isVideo ? (
            <iframe
              ref={frameRef} src={data.embed_url} title="Video" className="w-full h-full border-0"
              allow="autoplay; fullscreen" data-testid="file-preview-video-frame"
            />
          ) : isPreviewable ? (
            <iframe ref={frameRef} src={data.signed_url} title="Document" className="w-full h-full border-0" data-testid="file-preview-pdf-frame" />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-center px-6" data-testid="file-preview-unsupported">
              <AlertTriangle size={20} className="text-ink-soft" />
              <p className="text-ink-soft text-sm">This file type has no in-browser preview — download it instead.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
